/**
 * Le fuseau des horodatages uniFLOW — et pourquoi il a fallu le mesurer.
 *
 * uniFLOW Online écrit ses dates au format compact `AAAAMMJJhhmmss`, **sans aucune marque
 * de fuseau**. Rien dans la documentation ne dit ce que ces chiffres représentent.
 *
 * Mnemo les a d'abord pris pour de l'UTC. C'était faux, et la preuve est arithmétique :
 * le 21.09.2026 à 16 h 17 UTC (18 h 17 locales), la base contenait **130 travaux du jour
 * portant une heure postérieure à l'heure UTC courante**, le plus récent à 18 h 20. Des
 * travaux dans le futur n'existent pas ; ces chiffres sont donc de l'**heure locale
 * suisse**, et ils suivent le changement d'heure.
 *
 * Conséquence : on convertit à l'ingestion, une fois, vers l'instant UTC réel. La base ne
 * contient que des instants absolus — c'est la seule façon de comparer deux travaux
 * séparés par un passage à l'heure d'été sans se tromper d'une heure.
 *
 * ⚠️ Une erreur de fuseau ne se voit pas. Elle ne fait pas planter, elle ne remplit aucun
 * journal : elle décale tout d'une heure ou deux, et on ne s'en aperçoit que le jour où
 * quelqu'un compare un travail à ce qu'affiche le portail.
 */
/** Le fuseau dans lequel uniFLOW Online horodate, pour ce tenant. */
export const UNIFLOW_TIMEZONE = 'Europe/Zurich';
/**
 * Convertit une heure **murale** (telle qu'on la lirait sur une horloge du fuseau) en
 * instant UTC.
 *
 * Méthode : on part de l'hypothèse « ces chiffres sont de l'UTC », on demande à `Intl` ce
 * que cet instant donne dans le fuseau visé, on en déduit le décalage, et on corrige.
 * Deux passes suffisent, et la seconde n'a d'effet qu'aux abords d'un changement d'heure —
 * c'est précisément là qu'une seule passe se tromperait.
 */
export function wallTimeToUtc(year, month, day, hour, minute, second, timeZone = UNIFLOW_TIMEZONE) {
    const target = Date.UTC(year, month - 1, day, hour, minute, second);
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    /** Ce que l'instant `ms` affiche sur une horloge du fuseau, relu comme s'il était UTC. */
    const asWallClock = (ms) => {
        const parts = formatter.formatToParts(new Date(ms));
        const get = (type) => {
            const found = parts.find((p) => p.type === type);
            return found ? Number(found.value) : 0;
        };
        return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    };
    let utc = target;
    for (let pass = 0; pass < 2; pass++) {
        utc = target - (asWallClock(utc) - utc);
    }
    return new Date(utc);
}
/**
 * Ce qu'affiche une horloge du fuseau à l'instant `date`.
 *
 * L'inverse de `wallTimeToUtc`. Sert à décider « quel jour sommes-nous, et quelle heure
 * est-il **ici** » — une planification nocturne qui raisonnerait en UTC se déclencherait
 * à 1 h du matin l'été et à minuit l'hiver, sans que personne ne comprenne pourquoi.
 */
export function wallClockAt(date, timeZone = UNIFLOW_TIMEZONE) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(date);
    const get = (type) => {
        const found = parts.find((p) => p.type === type);
        return found ? Number(found.value) : 0;
    };
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
    };
}
/**
 * La période mensuelle « AAAA-MM » d'un instant, lue **à l'heure du mur suisse**.
 *
 * ⚠️ Ne pas confondre avec `periodOf` (csv.ts), qui lit l'UTC. Les deux existent, et la
 * différence a déjà coûté un mois entier :
 *
 * Un travail imprimé le **1ᵉʳ décembre à 00 h 07** en Suisse est, en UTC, le
 * **30 novembre à 23 h 07**. `periodOf` en déduit « 2025-11 » ; or la partition qui doit
 * l'accueillir est `print_job_2025_12`, dont les bornes sont posées en heure locale. Le
 * code assurait donc la mauvaise partition, puis insérait dans celle qui n'existait pas —
 * `no partition of relation "print_job" found for row`.
 *
 * 🪤 **Et le bogue est invisible sur une base en UTC** : les bornes y tombent alors au même
 * endroit que `periodOf`, et tout concorde. Il ne s'est manifesté qu'en production, dont
 * le PostgreSQL est en `Europe/Zurich`. C'est pourquoi les bornes ne sont plus laissées au
 * fuseau de la session : `mnemo_ensure_month_partition` le reçoit désormais en paramètre.
 *
 * Règle : **tout ce qui désigne un mois de données** — partition, période d'ingestion,
 * créneau de collecte — se lit à l'heure locale, comme le fichier que uniFLOW exporte.
 */
export function periodOfLocal(date, timeZone = UNIFLOW_TIMEZONE) {
    const { year, month } = wallClockAt(date, timeZone);
    return `${year}-${String(month).padStart(2, '0')}`;
}
